import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { T, useT } from "../i18n";
import { useRuf } from "../schale/Ruf";
import { FehlerBand, Knopf, Marke } from "./index";
import { useKataloge, useKunden, usePflichtfelder } from "../daten/haken";
import * as R from "../daten/routen";
import { uuid } from "../daten/speicher";
import type { Kunde, Pflichtfeld } from "../daten/typen";
import {
  KUNDENTYPEN, NUR_BEI_FIRMA, erkennbarFirma, pflichtGiltFuer, pflichtrolleFuer, wirksamerKundentyp,
} from "@mrmachine/fach";
import type { Kundentyp } from "@mrmachine/fach";
import { ustIdHinweis } from "../daten/ustid";

/* ══════════════════════════════════════════════════════════════════
   Das Kundenformular — ein Formular, drei Stellen.

   Bis zum 15.09.2026 lebte es in `masken/kunden/KundeNeu.tsx` und war
   nur über `/kunden/neu` zu erreichen. Jetzt steht es hier, weil es
   auch im Deckblatt gebraucht wird — aus der Schnellerfassung, aus dem
   Angebot, aus der Kundenliste. **Herausgehoben, nicht kopiert:** Zwei
   Formulare für denselben Kunden laufen auseinander, und dann fragt das
   eine nach der USt-IdNr. und das andere nicht.

   Ein Kunde geht nach Lexware und bekommt dort seine Nummer; ein
   Interessent bleibt hier liegen, bis ein Angebot angenommen wird.
   Darum hat der Interessent **deutlich weniger Felder**: keine
   USt-IdNr., kein Zahlungsziel, keine Rechnungsangaben. Was die
   Fakturierung braucht, wird beim Kunden trotzdem gleich gefragt — es
   später nachzutragen heißt, die erste Rechnung zu verzögern.

   **Keine leeren Felder auf Vorrat.** Zusatz, Telefon und E-Mail stehen
   nicht als drei leere Zeilen da, sondern als „+ Telefon", „+ E-Mail",
   „+ Zusatz" — wer sie braucht, klappt sie auf. Am Telefon zählt jede
   Zeile, die man nicht überspringen muss.

   Regel 11 — im Zweifel weniger fragen: Pflicht ist nur, was ohne
   Antwort nicht geht. Die Hauptadresse wird aus den Feldern darüber
   abgeleitet statt noch einmal gefragt, und ein Einsatzort ist
   freiwillig.

   Zwei Formen desselben Inhalts:
     · `seite` — der Dialogkasten auf `/kunden/neu`, mit eigenem Kopf
     · `blatt` — im Deckblatt; der Kopf gehört dort dem Blatt, der Fuß
       bleibt unten stehen, während die Mitte rollt
   ══════════════════════════════════════════════════════════════════ */

/**
 * Welche Rolle der Geschäftspartner bekommt, der hier entsteht.
 *
 * **Drei Rollen, ein Formular und eine Zeile in `customer`.** Der Vermieter
 * kam am 16.09.2026 dazu (Luca: *„Vermieter anlegen soll eine richtige Maske
 * anlegen wie bei Kunden, nur dass wir dann quasi einen Lieferanten
 * anlegen"*). Es ist **derselbe Datensatz**: Wer uns einen Teleskoplader
 * vermietet und nächste Woche selbst mietet, ist eine Zeile mit zwei Rollen —
 * genau dafür ist `is_vendor` ein Kennzeichen und keine zweite Tabelle
 * (Migration 0079). Ein eigenes Vermieterformular daneben wäre das zweite
 * Formular für dieselbe Sache.
 */
export type KundenArt = "kunde" | "interessent" | "vermieter";

/** `kunde` → `Kunde`, für die i18n-Schlüssel `kundeNeu.artKunde` und Geschwister. */
const gross = (wort: string): string => wort.charAt(0).toUpperCase() + wort.slice(1);

type Feld =
  | "firma" | "ust" | "steuer" | "telefon"
  | "zusatz" | "strasse" | "plz" | "ort" | "land"
  | "rechnungAn" | "zahlungsziel"
  | "person" | "tel" | "mail";

const LEER: Record<Feld, string> = {
  firma: "", ust: "", steuer: "", telefon: "",
  zusatz: "", strasse: "", plz: "", ort: "", land: "DE",
  rechnungAn: "", zahlungsziel: "",
  person: "", tel: "", mail: "",
};

/** Die Länder der Auswahl — ISO-2, weil die Anschrift und Lexware es so führen. */
const LAENDER = ["DE", "NL", "AT", "CH"] as const;

type OrtEntwurf = { id: number; name: string; strasse: string; plz: string; ort: string; notiz: string };

/** Für den Dublettenabgleich: Rechtsform und Interpunktion weg, damit
 *  „Hempen Agrar GmbH" und „Hempen Agrarservice e.K." aneinanderstoßen. */
function stamm(name: string): string {
  return name
    .toLowerCase()
    .replace(/\b(gmbh|ag|kg|ohg|gbr|mbh|co|e\.?\s?k|und)\b/g, " ")
    .replace(/[^a-z0-9äöüß]+/g, " ")
    .trim();
}

export function KundeFormular({
  art, artWechseln, form, abbrechen, angelegt, vorbelegt, schmutzigAendern,
}: {
  art: KundenArt;
  /**
   * Der Umschalter Kunde/Interessent/Lieferant oben im Formular.
   *
   * **Fehlt er, steht er nicht da** (21.09.2026). Wo die Rolle feststeht —
   * am Telefon entsteht beim Angebot und beim Aufmaß immer ein Interessent —,
   * wären drei Knöpfe ein Schalter, der nichts tut: gemessen auf 390 × 844,
   * drei Ziele zu 44 px ganz oben im Blatt, und keines bewegte etwas.
   *
   * Ein Umschalter, den man drückt und bei dem nichts passiert, ist
   * schlimmer als keiner: Beim zweiten Mal drückt man fester.
   */
  artWechseln?: ((neu: KundenArt) => void) | undefined;
  form: "seite" | "blatt";
  abbrechen: () => void;
  /** Der Kunde ist angelegt. Was danach passiert, entscheidet der Aufrufer:
   *  die Liste öffnen, oder ihn in der Maske darunter wählen. */
  angelegt: (kunde: Kunde) => void;
  /** Was schon bekannt ist — etwa der Name, der in der Suche stand. */
  vorbelegt?: { firma?: string };
  /** Meldet, ob etwas eingetippt ist, das verloren ginge. */
  schmutzigAendern?: (schmutzig: boolean) => void;
}) {
  const t = useT();
  const rufen = useRuf();
  const klient = useQueryClient();
  const id = useId();
  const interessent = art === "interessent";
  const vermieter = art === "vermieter";
  /* Was an Feldern gefragt wird, hängt nicht an der Rolle, sondern daran, ob
     wir ihm eine Rechnung schreiben. Dem Vermieter schreiben wir keine — er
     schickt uns eine —, aber USt-IdNr. und Anschrift braucht er trotzdem, und
     das Zahlungsziel ist dann SEINES. Darum dieselbe Fülle wie beim Kunden. */
  const knapp = interessent;

  /* **Was Pflicht ist, sagt der Betrieb** (Einstellungen · Pflichtfelder,
     Migration 0098). Luca, 16.09.2026: *„Pflichtfelder für Kundenanlage …
     und auch für Interessenten und auch für Vermieter."* Bis dahin stand es
     fest hier: Straße, PLZ und Ort beim Kunden und Vermieter — das ist auch
     die Vorbelegung, solange die Liste noch lädt.

     **Ein Pflichtfeld wird nie versteckt.** Beim Interessenten fehlen sonst
     Steuer- und Rechnungsangaben, Zusatz, Telefon und E-Mail stehen
     eingeklappt da. Ist eines davon Pflicht, steht es offen — ein Feld, das
     man ausfüllen muss und nicht sieht, wäre eine Sackgasse. Geprüft wird es
     beim Anlegen noch einmal im Server. */
  const { data: pflichtliste } = usePflichtfelder();

  /* ── Der Kundentyp (0187, Systemprüfung L2, 24.09.2026) ──
     Luca: *„Bei der Kundenanlage muss zwischen Privat- und Geschäftskunde
     unterschieden werden."* Lisa: *„per Auswahl gesetzt, nicht automatisch
     erkannt, und ohne Standardwert."* Also zwei Knöpfe, keiner vorgewählt —
     beim Kunden und beim Interessenten; der Vermieter braucht keinen, ihm
     schreiben wir keine Rechnung. **Der Grund für das Pflichtfeld** (Regel
     11): Davon hängt ab, welche Pflichtfelder gelten und ob nach einer
     USt-IdNr. gefragt wird. Solange nichts gewählt ist, rechnet das Formular
     mit dem, was der Name sagt — wie der Server bei einem Kunden ohne Typ. */
  const mitTyp = !vermieter;
  const [typ, setzeTyp] = useState<Kundentyp | "">("");
  const [typFehlt, setzeTypFehlt] = useState(false);
  const typKnopf = useRef<HTMLButtonElement | null>(null);
  /* Die USt-IdNr. sagt ihren Fehler am Feld, sobald man es verlässt — und
     beim Anlegen springt der Blick dorthin (08.10.2026, Kunde 10029). */
  const [ustGesehen, setzeUstGesehen] = useState(false);
  const ustFeld = useRef<HTMLInputElement | null>(null);

  const [f, setzeF] = useState<Record<Feld, string>>(() => ({
    ...LEER, firma: vorbelegt?.firma ?? "",
  }));
  const aendern = (name: Feld, wert: string) => setzeF((alt) => ({ ...alt, [name]: wert }));

  const wirksam = wirksamerKundentyp(typ || null, f.firma);
  const pflichtig = new Set<Pflichtfeld>(
    pflichtliste?.[pflichtrolleFuer(art, wirksam)] ?? (knapp ? [] : ["strasse", "plz", "ort"]),
  );
  /* **Ein Stern, kein Wort.** Luca, 16.09.2026, vor „Straße, Nr. Pflicht":
     *„Das sieht natürlich total dämlich aus."* Der Stern ist das Zeichen, das
     jeder aus Formularen kennt. Für den Schirmleser steht er nicht da
     (`aria-hidden`) — der liest `required` am Feld selbst vor. */

  /* **Das Zahlungsziel kommt aus der Liste** (Migration 0106, Durchgang ST2)
     und ist mit ihrem Standard vorbelegt (Regel 11). Bis zum 16.09.2026 war
     es ein leeres Zahlenfeld, und ohne Angabe galten fest 14 Tage — obwohl
     der Satz darunter „das Zahlungsziel des Betriebs" versprach. Wer nichts
     anfasst, bekommt den Standard; wer „ohne Angabe" wählt, das, was der
     Server dann nimmt (Standard, sonst die Betriebseinstellung). */
  const { data: kataloge } = useKataloge();
  const zielliste = (kataloge?.zahlungsziele ?? []).filter((z) => z.aktiv);
  const zielStandard = zielliste.find((z) => z.standard);
  const [zielAngefasst, setzeZielAngefasst] = useState(false);

  /* **USt-IdNr. und Steuernummer nur beim Geschäftskunden** (Systemprüfung
     A-03, seit 0187 am gewählten Typ). Lisa: *„Bei einem Privatkunden gibt es
     die in aller Regel nicht."* Der Server prüft mit derselben Regel
     (`pflichtGiltFuer`). Wer „Privat" gewählt hat, sieht die beiden Felder
     gar nicht mehr. */
  const pflicht = (feld: Pflichtfeld) => pflichtig.has(feld) && pflichtGiltFuer(feld, wirksam);
  const steuerNurBeiFirma = NUR_BEI_FIRMA.some((feld) => pflichtig.has(feld));
  const pflichtMarke = (...felder: Pflichtfeld[]) => (felder.some(pflicht)
    ? <span className="pflicht-stern" aria-hidden="true" title={t("allgemein.pflicht")}>{t("allgemein.pflichtStern")}</span>
    : null);
  const zeigtSteuer = typ !== "privat" && (!knapp || pflicht("ust_id") || pflicht("steuernummer"));
  const ustHinweis = ustIdHinweis(f.ust);
  const zeigtRechnung = !knapp || pflicht("rechnung_email") || pflicht("zahlungsziel_tage");
  /* Unangefasst steht der Standard da; angefasst, was gewählt ist — auch „ohne Angabe". */
  const zielWert = zielAngefasst ? f.zahlungsziel : (f.zahlungsziel || (zielStandard ? String(zielStandard.tage) : ""));

  /* Was aufgeklappt ist. Ein Feld, das jemand aufgeklappt hat, bleibt auf —
     auch wenn es leer ist: Wer „+ Telefon" gedrückt hat, will es tippen. */
  const [zeigt, setzeZeigt] = useState({ zusatz: false, tel: false, mail: false });
  const aufklappen = (was: keyof typeof zeigt) => setzeZeigt((alt) => ({ ...alt, [was]: true }));

  const [orte, setzeOrte] = useState<OrtEntwurf[]>([]);
  const naechste = useRef(1);

  /* Schmutzig ist das Formular, sobald etwas darin steht, das nicht schon
     beim Öffnen dastand. Der vorbelegte Name zählt nicht — den hat der
     Anwender nicht hier getippt, sondern in der Suche. */
  const schmutzig = useMemo(() => {
    const anfang = { ...LEER, firma: vorbelegt?.firma ?? "" };
    return orte.length > 0 || typ !== ""
      || (Object.keys(f) as Feld[]).some((k) => f[k] !== anfang[k]);
  }, [f, orte, vorbelegt, typ]);
  useEffect(() => { schmutzigAendern?.(schmutzig); }, [schmutzig, schmutzigAendern]);

  /* Ein Abruf trägt beides: den Zähler im Fuß und den Dublettenabgleich.
     Bei einem Stamm in Zehntausenderhöhe gehört der Abgleich auf den
     Server (`GET /kunden?suche=`); solange die Liste in einen Abruf passt,
     ist eine zweite Anfrage je Anschlag der teurere Weg. */
  /* **Mit den Deaktivierten** (Migration 0100): Eine aufgegebene Firma, die
     wieder anruft, soll als Dublette auffallen und nicht neu angelegt werden.
     Gezählt werden dagegen nur die aktiven. */
  const { data: bestand } = useKunden(undefined, undefined, true);
  const aktive = bestand?.zeilen.filter((k) => !k.deaktiviert) ?? [];
  const kundenZahl = aktive.filter((k) => k.art === "kunde").length;
  const interessentenZahl = aktive.filter((k) => k.art === "interessent").length;
  /* N4-18: Beim Lieferanten zählt der Fuß Lieferanten — „3 Kunden ·
     1 Interessenten" stand auch dort. */
  const lieferantenZahl = aktive.filter((k) => k.art === "vermieter").length;

  const dublette = useMemo(() => {
    const wort = stamm(f.firma);
    if (wort.length < 3) return undefined;
    return bestand?.zeilen.find((k) => {
      const andere = stamm(k.name);
      return andere !== "" && (andere.startsWith(wort) || wort.startsWith(andere));
    });
  }, [f.firma, bestand]);

  /* **Ein Schlüssel je Formular** (0218, Playbook K-01): Kommt derselbe
     Kunde zweimal an — Doppelklick, Wiederholung —, legt der Server ihn
     einmal an, und drüben entsteht ein Kontakt, nicht zwei. Nach einem
     Fehler gilt er weiter: Der gescheiterte Versuch hat nichts hinterlassen. */
  const [erfassungId] = useState(uuid);
  /** Läuft das Anlegen schon? Ein Ref, weil `isPending` erst nach dem Zeichnen kommt. */
  const sendet = useRef(false);

  const anlegen = useMutation({
    mutationFn: (neu: R.KundeEingabe) => R.kundeAnlegen(neu),
    onSettled: () => { sendet.current = false; },
    onSuccess: (neuer) => {
      // Der Schlüssel `["kunden", suche, nur]` aus `daten/haken.ts` —
      // sein Kopf trifft jede Auswahl, egal welche Suche gerade offen ist.
      void klient.invalidateQueries({ queryKey: ["kunden"] });
      /* **Der Kontakt in Lexware entsteht im selben Zug** (dienste/kontaktsofort.ts).
         Die Nummer in der Meldung ist deshalb schon die von drüben. Hat
         Lexware nicht geantwortet, sagt die Meldung es — sonst fiele es erst
         in der Akte auf, an „noch ein Vorschlag". */
      if (!interessent && !neuer.drueben) {
        rufen("kundeAngelegtOhneLexware", { name: neuer.name, nr: neuer.nr }, "acht");
      } else if (vermieter) rufen("vermieterAngelegt", { name: neuer.name });
      else if (interessent) rufen("interessentAngelegt");
      else rufen("kundeAngelegt", { name: neuer.name, nr: neuer.nr });
      angelegt(neuer);
    },
  });

  const absenden = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    /* Der Typ ist die eine Wahl, die kein Feld des Browsers prüft — zwei
       Knöpfe haben kein `required`. Fehlt er, springt der Blick dorthin. */
    if (mitTyp && !typ) {
      setzeTypFehlt(true);
      typKnopf.current?.focus();
      return;
    }
    /* **Was gefragt wird, wird auch gespeichert.** Bis zum 16.09.2026 gingen
       drei Felder hinüber — Firma, Ort, Art. Gefragt wurden zwölf. USt-IdNr.,
       Straße, PLZ, Rechnungs-E-Mail, Zahlungsziel, Telefon und
       Ansprechpartner wurden getippt und verworfen, und in der Akte stand
       danach überall ein Strich. Wer ein Feld fragt, speichert es auch —
       sonst gehört es nicht ins Formular. */
    if (zeigtSteuer && ustIdHinweis(f.ust)) {
      setzeUstGesehen(true);
      ustFeld.current?.focus();
      return;
    }
    if (sendet.current) return;
    sendet.current = true;
    anlegen.mutate({
      erfassung_id: erfassungId,
      firma: f.firma.trim(),
      /* Ein reiner Vermieter ist (noch) kein Kunde: Er hat nichts gemietet.
         `is_prospect` bleibt darum stehen — und die Liste zeigt ihn trotzdem
         als Vermieter, weil die Rolle die genauere Auskunft ist. */
      interessent: vermieter ? true : interessent,
      ...(vermieter ? { vermieter: true } : {}),
      ...(mitTyp && typ ? { kundentyp: typ } : {}),
      anschrift: {
        ...(f.zusatz.trim() ? { zusatz: f.zusatz.trim() } : {}),
        ...(f.strasse.trim() ? { strasse: f.strasse.trim() } : {}),
        ...(f.plz.trim() ? { plz: f.plz.trim() } : {}),
        ...(f.ort.trim() ? { ort: f.ort.trim() } : {}),
        land: f.land.trim() || "DE",
      },
      ...(f.telefon.trim() ? { telefon: f.telefon.trim() } : {}),
      // Was zu sehen war, geht mit — beim Interessenten also auch Steuer- und
      // Rechnungsangaben, wenn der Betrieb sie zur Pflicht gemacht hat.
      ...(zeigtSteuer ? {
        ...(f.ust.trim() ? { ust_id: f.ust.trim() } : {}),
        ...(f.steuer.trim() ? { steuernummer: f.steuer.trim() } : {}),
      } : {}),
      ...(zeigtRechnung ? {
        ...(f.rechnungAn.trim() ? { rechnung_email: f.rechnungAn.trim() } : {}),
        ...(zielWert ? { zahlungsziel_tage: Number(zielWert) } : {}),
      } : {}),
      ...(f.person.trim() ? { ansprechpartner: f.person.trim() } : {}),
      ...(f.tel.trim() ? { ansprechpartner_telefon: f.tel.trim() } : {}),
      ...(f.mail.trim() ? { ansprechpartner_email: f.mail.trim() } : {}),
    }, {
      /* Die Einsatzorte gehen als eigene Aufrufe hinterher: `POST /kunden`
         kennt sie nicht, und jeder Ort zieht drüben eine eigene Nummer. Der
         erste wird der Standard — er ist das, was die Schnellerfassung
         vorbelegt (Regel 11). */
      onSuccess: (neuer) => {
        (vermieter ? [] : orte).filter((o) => o.name.trim()).forEach((o, i) => {
          R.ortAnlegen(neuer.nr, {
            bezeichnung: o.name.trim(),
            anschrift: { strasse: o.strasse.trim(), plz: o.plz.trim(), ort: o.ort.trim() },
            hinweis: o.notiz.trim() || null,
            standard: i === 0,
          }).catch(() => {
            /* **Der Kunde steht, der Ort nicht — das muss man sehen**
               (17.09.2026). Bis hierher lief der Fehler ins Leere: Das Blatt
               war zu, der Kunde da, und dass sein Einsatzort fehlt, merkte
               man erst beim nächsten Angebot. Der Satz nennt den Ort und den
               Weg: in der Akte nachtragen. */
            rufen("ortNichtAngelegt", { name: o.name.trim() }, "stopp");
          });
        });
      },
    });
  };

  const ortDazu = () => {
    setzeOrte((alt) => [
      ...alt,
      { id: naechste.current++, name: "", strasse: "", plz: "", ort: "", notiz: "" },
    ]);
    rufen("ortDazu");
  };

  const ortWeg = (weg: number) => {
    setzeOrte((alt) => alt.filter((o) => o.id !== weg));
    rufen("ortWeg", undefined, "acht");
  };

  const ortAendern = (welcher: number, feld: keyof Omit<OrtEntwurf, "id">, wert: string) =>
    setzeOrte((alt) => alt.map((o) => (o.id === welcher ? { ...o, [feld]: wert } : o)));

  /* Die Hauptadresse wird nicht gefragt, sie wird abgelesen — sie steht
     drei Gruppen weiter oben. Regel 11. */
  const hauptzeile = [f.strasse, [f.plz, f.ort].filter(Boolean).join(" ")]
    .filter(Boolean).join(` ${t("allgemein.trennpunkt")} `);

  const imBlatt = form === "blatt";

  const rumpf = (
    <>
      {/* Im Blatt trägt der Kopf des Blatts Titel und Untertitel — hier
          noch einmal wäre derselbe Satz zweimal untereinander. */}
      {imBlatt ? null : (
        <div className="fk">
          <h4>{t(`kundeNeu.dialog${gross(art)}`)}</h4>
          <p>{t(`kundeNeu.unter${gross(art)}`)}</p>
        </div>
      )}

      <div className="fb">
        {/* Drei Knöpfe statt zwei. Der Vermieter steht daneben und nicht in
            einem eigenen Formular: Es ist derselbe Datensatz, und wer beim
            Tippen merkt, dass die Firma auch Kunde ist, schaltet hier um,
            statt von vorn anzufangen. */}
        {artWechseln ? (
          <div className="wahl">
            {(["kunde", "interessent", "vermieter"] as const).map((a) => (
              <button
                key={a}
                type="button"
                aria-pressed={art === a}
                style={{ flex: 1 }}
                onClick={() => artWechseln(a)}
              >
                {t(`kundeNeu.art${gross(a)}`)}
              </button>
            ))}
          </div>
        ) : null}
        {/* Beim Lieferanten steht kein Satz mehr (Durchgang 16.09.2026, ST1):
            „Derselbe Stammsatz wie ein Kunde, nur mit einer zweiten Rolle"
            erklärte die Technik, nicht die Sache. */}
        {vermieter ? null : (
          <div className="fhint">
            <T k={`kundeNeu.hinweis${gross(art)}`} />
          </div>
        )}
      </div>

      <div className="fgruppen-raum">

        {/* ─── Firma ─── */}
        <div className="fgruppe">
          <span className="fgruppe-schild">{t("kundeNeu.gruppeFirma")}</span>
          <div className="fb">
            <div className="frow">
              <label htmlFor={`${id}-firma`}>{t("kundeNeu.firma")}</label>
              {/* Das einzige Pflichtfeld beider Wege: Ohne Namen gibt es
                  nichts anzulegen. */}
              <input
                className="fein"
                id={`${id}-firma`}
                autoComplete="organization"
                required
                value={f.firma}
                onChange={(e) => aendern("firma", e.target.value)}
              />
            </div>
            {dublette ? (
              <div className="fhint acht">
                <T k="kundeNeu.dublette" werte={{ name: dublette.name }} />{" "}
                <Link
                  to="/kunden/$nr"
                  params={{ nr: dublette.nr }}
                  className="knopf klein stumm"
                >
                  {t("allgemein.ansehen")}
                </Link>
              </div>
            ) : null}

            {/* ─── Der Kundentyp (0187) — zwei Knöpfe, keiner vorgewählt ─── */}
            {mitTyp ? (
              <div className="frow">
                <label id={`${id}-typ`} htmlFor={`${id}-typ-0`}>
                  {t("kundeNeu.typ")}
                  <span className="pflicht-stern" aria-hidden="true" title={t("allgemein.pflicht")}>{t("allgemein.pflichtStern")}</span>
                </label>
                <div className="wahl" role="group" aria-labelledby={`${id}-typ`}>
                  {KUNDENTYPEN.map((k, i) => (
                    <button
                      key={k}
                      id={`${id}-typ-${i}`}
                      type="button"
                      aria-pressed={typ === k}
                      style={{ flex: 1 }}
                      ref={i === 0 ? typKnopf : undefined}
                      onClick={() => { setzeTyp(k); setzeTypFehlt(false); }}
                    >
                      {t(`kunde.typ.${k}`)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {mitTyp && !typ && (typFehlt || erkennbarFirma(f.firma)) ? (
              <div className="fhint acht" role={typFehlt ? "alert" : undefined}>
                {t(typFehlt ? "kundeNeu.typWaehlen" : "kundeNeu.typVorschlag")}
              </div>
            ) : null}

            {/* **Die Rechtsform hat kein eigenes Feld mehr** (16.09.2026).
                Sie stand als Auswahl da und wurde beim Speichern verworfen —
                es gibt keine Spalte dafür, und es kommt auch keine. Drüben
                trägt genau ein Feld den Namen (`company.name`), und das ist
                die Firmierung, die auf der Rechnung steht. Zwei Felder hier
                hießen, bei jedem Beleg zu entscheiden, ob „Ahlers Bau" und
                „GmbH" zusammengesetzt werden — und irgendwann steht „Ahlers
                Bau GmbH GmbH" auf einem Papier. Designkonzept, Leitsatz 4. */}

            <div className="frow">
              <label htmlFor={`${id}-telefon`}>{t("kundeNeu.zentrale")}{pflichtMarke("telefon")}</label>
              <input
                className="fein mono"
                id={`${id}-telefon`}
                autoComplete="tel"
                type="tel"
                required={pflicht("telefon")}
                value={f.telefon}
                onChange={(e) => aendern("telefon", e.target.value)}
              />
            </div>

            {zeigtSteuer ? (
              <>
                <div className="frow">
                  <label htmlFor={`${id}-ust`}>{t("kundeNeu.ustIdNr")}{pflichtMarke("ust_id")}</label>
                  <input
                    className="fein mono"
                    id={`${id}-ust`}
                    required={pflicht("ust_id")}
                    ref={ustFeld}
                    placeholder={t("kundeNeu.ustPlatz")}
                    value={f.ust}
                    aria-invalid={ustGesehen && ustHinweis ? true : undefined}
                    aria-describedby={ustGesehen && ustHinweis ? `${id}-ust-fehler` : undefined}
                    onChange={(e) => aendern("ust", e.target.value)}
                    onBlur={() => setzeUstGesehen(true)}
                  />
                </div>
                {ustGesehen && ustHinweis ? (
                  <div className="fhint acht" id={`${id}-ust-fehler`} role="alert">
                    {t(ustHinweis.pfad, ustHinweis.werte)}
                  </div>
                ) : null}
                <div className="frow">
                  <label htmlFor={`${id}-steuer`}>{t("kundeNeu.steuernummer")}{pflichtMarke("steuernummer")}</label>
                  <input
                    className="fein mono"
                    id={`${id}-steuer`}
                    required={pflicht("steuernummer")}
                    value={f.steuer}
                    onChange={(e) => aendern("steuer", e.target.value)}
                  />
                </div>
                <div className="fhint">{t("kundeNeu.ustGeprueft")}</div>
                {/* Nur wenn der Betrieb sie zur Pflicht gemacht hat: Der Satz
                    sagt, warum der Stern kommt und geht. */}
                {/* Ein Lieferant hat keinen Kundentyp (K-07). */}
                {steuerNurBeiFirma && !vermieter ? (
                  <div className="fhint">
                    {t(wirksam === "gewerblich" ? "kundeNeu.steuerPflichtFirma" : "kundeNeu.steuerFreiPrivat")}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>

        {/* ─── Rechnungsanschrift ─── */}
        <div className="fgruppe">
          <span className="fgruppe-schild">{t("kundeNeu.gruppeAnschrift")}</span>
          <div className="fb">
            {/* Der Zusatz ist der seltene Fall — ein Postfach, eine
                Abteilung. Er steht darum nicht als leeres Feld da, sondern
                als „+ Zusatz", bis ihn jemand braucht. */}
            {zeigt.zusatz || f.zusatz || pflicht("zusatz") ? (
              <>
                <div className="frow">
                  <label htmlFor={`${id}-zusatz`}>
                    {t("kundeNeu.zusatz")}
                    {pflicht("zusatz")
                      ? pflichtMarke("zusatz")
                      : <> <span className="leise">{t("allgemein.freiwillig")}</span></>}
                  </label>
                  <input
                    className="fein"
                    id={`${id}-zusatz`}
                autoComplete="address-line2"
                    required={pflicht("zusatz")}
                    placeholder={t("kundeNeu.zusatzPlatz")}
                    value={f.zusatz}
                    onChange={(e) => aendern("zusatz", e.target.value)}
                  />
                </div>
                <div className="fhint">{t("kundeNeu.zusatzHinweis")}</div>
              </>
            ) : null}

            <div className="frow">
              <label htmlFor={`${id}-strasse`}>{t("kundeNeu.strasse")}{pflichtMarke("strasse")}</label>
              {/* Beim Kunden Pflicht, beim Interessenten nicht: Auf eine
                  Rechnung gehört eine Anschrift, auf ein Angebot genügt
                  der Name, bis es angenommen wird. */}
              <input
                className="fein"
                id={`${id}-strasse`}
                autoComplete="street-address"
                required={pflicht("strasse")}
                value={f.strasse}
                onChange={(e) => aendern("strasse", e.target.value)}
              />
            </div>

            <div className="frow">
              <label htmlFor={`${id}-plz`}>{t("kundeNeu.plzOrt")}{pflichtMarke("plz", "ort")}</label>
              <div className="plz-ort">
                <input
                  className="fein mono"
                  id={`${id}-plz`}
                  autoComplete="postal-code"
                  inputMode="numeric"
                  required={pflicht("plz")}
                  aria-label={t("kundeNeu.plz")}
                  value={f.plz}
                  onChange={(e) => aendern("plz", e.target.value)}
                />
                <input
                  className="fein"
                  autoComplete="address-level2"
                  required={pflicht("ort")}
                  aria-label={t("kundeNeu.ort")}
                  value={f.ort}
                  onChange={(e) => aendern("ort", e.target.value)}
                />
              </div>
            </div>

            <div className="frow">
              <label htmlFor={`${id}-land`}>{t("kundeNeu.land")}</label>
              {/* **Der Wert ist der ISO-2-Code, nicht das Wort.** Bis zum
                  16.09.2026 war beides dasselbe: Die Auswahl trug
                  „Deutschland" als Wert, und in der Anschrift stand danach
                  `land: "Deutschland"` — während Lexware Office `countryCode`
                  erwartet und das Datenmodell ISO-2 vorschreibt. Der Name
                  liegt in de.json, der Code im Code. */}
              <select
                className="fein"
                id={`${id}-land`}
                value={f.land}
                onChange={(e) => aendern("land", e.target.value)}
              >
                {LAENDER.map((c) => (
                  <option key={c} value={c}>{t(`kundeNeu.laender.${c}`)}</option>
                ))}
              </select>
            </div>

            {zeigt.zusatz || f.zusatz || pflicht("zusatz") ? null : (
              <div className="fdazu">
                <button type="button" className="fdazu-knopf" onClick={() => aufklappen("zusatz")}>
                  {t("kundeNeu.zusatzDazu")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ─── Rechnung — nur beim Kunden ─── */}
        {zeigtRechnung ? (
          <div className="fgruppe">
            <span className="fgruppe-schild">{t("kundeNeu.gruppeRechnung")}</span>
            <div className="fb">
              <div className="frow">
                <label htmlFor={`${id}-rechnung`}>{t("kundeNeu.rechnungAn")}{pflichtMarke("rechnung_email")}</label>
                <input
                  className="fein"
                  id={`${id}-rechnung`}
                  type="email"
                  required={pflicht("rechnung_email")}
                  value={f.rechnungAn}
                  onChange={(e) => aendern("rechnungAn", e.target.value)}
                />
              </div>
              <div className="fhint">{t("kundeNeu.rechnungAnHinweis")}</div>

              <div className="frow">
                <label htmlFor={`${id}-ziel`}>{t("kundeNeu.zahlungsziel")}{pflichtMarke("zahlungsziel_tage")}</label>
                {/* Eine Auswahl aus der Liste unter Weiteres, vorbelegt mit
                    ihrem Standard. Die Zahlen darin sind gepflegt, nicht hier
                    erfunden — die Liste selbst ist bis zur Kalkulation aus
                    04-preisfindung.md ein Beispiel (Migration 0106). */}
                <select
                  className="fein"
                  id={`${id}-ziel`}
                  required={pflicht("zahlungsziel_tage")}
                  value={zielWert}
                  onChange={(e) => { setzeZielAngefasst(true); aendern("zahlungsziel", e.target.value); }}
                >
                  <option value="">{t(pflicht("zahlungsziel_tage") ? "zahlungsziele.bitteWaehlen" : "zahlungsziele.ohneAngabe")}</option>
                  {zielliste.map((z) => (
                    <option key={z.id} value={String(z.tage)}>{z.label}</option>
                  ))}
                </select>
              </div>
              <div className="fhint">{t("kundeNeu.zahlungszielHinweis")}</div>
            </div>
          </div>
        ) : null}

        {/* ─── Ansprechpartner ─── */}
        <div className="fgruppe">
          <span className="fgruppe-schild">{t("kundeNeu.gruppeAnsprechpartner")}</span>
          <div className="fb">
            <div className="frow">
              <label htmlFor={`${id}-person`}>{t("kundeNeu.name")}{pflichtMarke("ansprechpartner")}</label>
              <input
                className="fein"
                id={`${id}-person`}
                autoComplete="name"
                required={pflicht("ansprechpartner")}
                value={f.person}
                onChange={(e) => aendern("person", e.target.value)}
              />
            </div>

            {/* Telefon und E-Mail kommen auf Zuruf, nicht auf Vorrat. Am
                Telefon hat man meist nur eines von beiden — und ein leeres
                Feld ist eines, das man überspringen muss. */}
            {zeigt.tel || f.tel || pflicht("ansprechpartner_telefon") ? (
              <div className="frow">
                <label htmlFor={`${id}-tel`}>{t("kundeNeu.telefon")}{pflichtMarke("ansprechpartner_telefon")}</label>
                <input
                  className="fein mono"
                  id={`${id}-tel`}
                  autoComplete="tel"
                  required={pflicht("ansprechpartner_telefon")}
                  type="tel"
                  value={f.tel}
                  onChange={(e) => aendern("tel", e.target.value)}
                />
              </div>
            ) : null}

            {zeigt.mail || f.mail || pflicht("ansprechpartner_email") ? (
              <>
                <div className="frow">
                  <label htmlFor={`${id}-mail`}>{t("kundeNeu.mail")}{pflichtMarke("ansprechpartner_email")}</label>
                  <input
                    className="fein"
                    id={`${id}-mail`}
                    autoComplete="email"
                    required={pflicht("ansprechpartner_email")}
                    type="email"
                    value={f.mail}
                    onChange={(e) => aendern("mail", e.target.value)}
                  />
                </div>
                <div className="fhint">{t(vermieter ? "kundeNeu.mailHinweisLieferant" : "kundeNeu.mailHinweis")}</div>
              </>
            ) : null}

            {(zeigt.tel || f.tel || pflicht("ansprechpartner_telefon")) && (zeigt.mail || f.mail || pflicht("ansprechpartner_email")) ? null : (
              <div className="fdazu">
                {zeigt.tel || f.tel || pflicht("ansprechpartner_telefon") ? null : (
                  <button type="button" className="fdazu-knopf" onClick={() => aufklappen("tel")}>
                    {t("kundeNeu.telefonDazu")}
                  </button>
                )}
                {zeigt.mail || f.mail || pflicht("ansprechpartner_email") ? null : (
                  <button type="button" className="fdazu-knopf" onClick={() => aufklappen("mail")}>
                    {t("kundeNeu.mailDazu")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ─── Einsatzorte ─── — beim Lieferanten nicht: Er liefert uns,
            wir ihm nicht (K-07). */}
        {vermieter ? null : (
        <div className="fgruppe fgruppe-breit">
          <span className="fgruppe-schild">
            {t("kundeNeu.gruppeOrte")} <span className="leise">{t("allgemein.freiwillig")}</span>
          </span>
          <div className="fb">
            <div className="ort-zeile ort-haupt">
              <span className="ort-pkt" aria-hidden="true">1</span>
              <span className="ort-text">
                <b>{t("kundeNeu.hauptadresse")}</b>
                <span>{hauptzeile || t("allgemein.strich")}</span>
              </span>
              <Marke>{t("kundeNeu.standard")}</Marke>
            </div>

            <p className="akte-fussnote"><T k="kundeNeu.orteSatz" /></p>

            {orte.map((o, i) => (
              <div className="ort-block" key={o.id}>
                <div className="ort-block-kopf">
                  <span className="ort-pkt" aria-hidden="true">{i + 2}</span>
                  <input
                    className="fein"
                    value={o.name}
                    placeholder={t("kundeNeu.ortNamePlatz")}
                    aria-label={t("kundeNeu.ortName", { n: i + 2 })}
                    onChange={(e) => ortAendern(o.id, "name", e.target.value)}
                  />
                  <Knopf
                    klein
                    art="stumm"
                    aria-label={t("kundeNeu.ortEntfernen")}
                    onClick={() => ortWeg(o.id)}
                  >
                    <span aria-hidden="true">{t("allgemein.schliessen")}</span>
                  </Knopf>
                </div>
                <div className="ort-felder">
                  <div className="frow">
                    <label htmlFor={`${id}-o${o.id}-strasse`}>{t("kundeNeu.strasse")}</label>
                    <input
                      className="fein"
                      id={`${id}-o${o.id}-strasse`}
                      placeholder={t("kundeNeu.ortStrassePlatz")}
                      value={o.strasse}
                      onChange={(e) => ortAendern(o.id, "strasse", e.target.value)}
                    />
                  </div>
                  <div className="frow">
                    <label htmlFor={`${id}-o${o.id}-plz`}>{t("kundeNeu.plzOrt")}</label>
                    <div className="plz-ort">
                      <input
                        className="fein mono"
                        id={`${id}-o${o.id}-plz`}
                        inputMode="numeric"
                        placeholder={t("kundeNeu.ortPlzPlatz")}
                        aria-label={t("kundeNeu.plz")}
                        value={o.plz}
                        onChange={(e) => ortAendern(o.id, "plz", e.target.value)}
                      />
                      <input
                        className="fein"
                        placeholder={t("kundeNeu.ortOrtPlatz")}
                        aria-label={t("kundeNeu.ort")}
                        value={o.ort}
                        onChange={(e) => ortAendern(o.id, "ort", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="frow">
                    <label htmlFor={`${id}-o${o.id}-notiz`}>{t("kundeNeu.ortHinweis")}</label>
                    <input
                      className="fein"
                      id={`${id}-o${o.id}-notiz`}
                      placeholder={t("kundeNeu.ortHinweisPlatz")}
                      value={o.notiz}
                      onChange={(e) => ortAendern(o.id, "notiz", e.target.value)}
                    />
                  </div>
                  <div className="fhint"><T k="kundeNeu.ortHinweisSatz" /></div>
                </div>
              </div>
            ))}

            <Knopf klein style={{ alignSelf: "flex-start" }} onClick={ortDazu}>
              {t("kundeNeu.ortDazu")}
            </Knopf>
          </div>
        </div>
        )}

      </div>

      <div className="fb">
        <div className="nachtrag">
          <T k={`kundeNeu.nachtrag${gross(art)}`} />
        </div>
      </div>

      {anlegen.isError ? (
        <FehlerBand
          fehler={anlegen.error}
          nochmal={() => anlegen.reset()}
        />
      ) : null}
    </>
  );

  const fuss = (
    <footer className={imBlatt ? "deck-fuss" : undefined}>
      <span className="weg">
        {vermieter
          ? t(lieferantenZahl === 1 ? "kundeNeu.zaehlerLieferantEins" : "kundeNeu.zaehlerLieferanten", { zahl: lieferantenZahl })
          : t("kundeNeu.zaehler", { kunden: kundenZahl, interessenten: interessentenZahl })}
      </span>
      <Knopf onClick={abbrechen}>{t("allgemein.abbrechen")}</Knopf>
      <Knopf type="submit" art="haupt" disabled={anlegen.isPending}>
        {/* Ein Kunde geht beim Anlegen nach Lexware — das dauert einen Atemzug,
            und der Knopf sagt, worauf gewartet wird. */}
        {anlegen.isPending && !interessent
          ? t("kundeNeu.legtAnMitLexware")
          : t(`kundeNeu.anlegen${gross(art)}`)}
      </Knopf>
    </footer>
  );

  /* Im Blatt rollt die Mitte, und der Fuß mit „Anlegen" bleibt stehen —
     wer unten angekommen ist, soll den Knopf nicht erst suchen müssen. Auf
     der Seite ist das Formular ein Kasten, der mit der Seite rollt. */
  if (imBlatt) {
    return (
      <form className="kundenform deck-form" onSubmit={absenden} noValidate={false}>
        <div className="deck-form-leib">
          <div className="deck-form-innen">{rumpf}</div>
        </div>
        {fuss}
      </form>
    );
  }

  return (
    <form className="fdialog kundenform" onSubmit={absenden} noValidate={false}>
      {rumpf}
      {fuss}
    </form>
  );
}
